import { useState } from 'react';
import { Modal, FormRow, FormSection, Button } from '@shared/components';
import type { SubcontractorCreateInput } from '../services/subcontract.service';
import type { Subcontractor } from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  subcontractor?: Subcontractor | null;
  onSubmit: (data: SubcontractorCreateInput) => Promise<void>;
}

const cls = 'w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';

export function SubcontractorFormModal({ isOpen, onClose, subcontractor, onSubmit }: Props) {
  const [form, setForm] = useState<SubcontractorCreateInput>({
    name: subcontractor?.name ?? '',
    email: subcontractor?.email ?? null,
    phone: subcontractor?.phone ?? null,
    postalCode: subcontractor?.postalCode ?? null,
    prefecture: subcontractor?.prefecture ?? '',
    addr1: subcontractor?.addr1 ?? '',
    addr2: subcontractor?.addr2 ?? '',
    addr3: subcontractor?.addr3 ?? '',
    department: subcontractor?.department ?? '',
    paymentTerms: subcontractor?.paymentTerms ?? 30,
    notes: subcontractor?.notes ?? null,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const upd = (patch: Partial<SubcontractorCreateInput>) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try { await onSubmit(form); onClose(); }
    catch (err) { setError(err instanceof Error ? err.message : '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={subcontractor ? '外注先編集' : '外注先登録'} size="lg">
      <form onSubmit={handleSubmit}>
        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
        <FormSection title="基本情報">
          <FormRow label="外注先名" required>
            <input className={cls} value={form.name} onChange={(e) => upd({ name: e.target.value })} required />
          </FormRow>
          <FormRow label="メール">
            <input type="email" className={cls} value={form.email ?? ''} onChange={(e) => upd({ email: e.target.value || null })} />
          </FormRow>
          <FormRow label="電話番号">
            <input className={cls} value={form.phone ?? ''} onChange={(e) => upd({ phone: e.target.value || null })} />
          </FormRow>
        </FormSection>
        <FormSection title="住所・支払">
          <FormRow label="郵便番号">
            <input className={cls} value={form.postalCode ?? ''} onChange={(e) => upd({ postalCode: e.target.value || null })} />
          </FormRow>
          <FormRow label="都道府県">
            <input className={cls} value={form.prefecture} onChange={(e) => upd({ prefecture: e.target.value })} />
          </FormRow>
          <FormRow label="住所（市区町村）">
            <input className={cls} value={form.addr1} onChange={(e) => upd({ addr1: e.target.value })} />
          </FormRow>
          <FormRow label="住所（番地）">
            <input className={cls} value={form.addr2} onChange={(e) => upd({ addr2: e.target.value })} />
          </FormRow>
          <FormRow label="住所（建物名・フロア）">
            <input className={cls} value={form.addr3} onChange={(e) => upd({ addr3: e.target.value })} />
          </FormRow>
          <FormRow label="部署">
            <input className={cls} value={form.department} onChange={(e) => upd({ department: e.target.value })} />
          </FormRow>
          <FormRow label="支払サイト（日）">
            <input type="number" className={cls} value={form.paymentTerms} onChange={(e) => upd({ paymentTerms: Number(e.target.value) })} min={0} />
          </FormRow>
        </FormSection>
        <FormSection title="備考">
          <FormRow label="備考" span>
            <textarea className={cls} value={form.notes ?? ''} onChange={(e) => upd({ notes: e.target.value || null })} rows={2} />
          </FormRow>
        </FormSection>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button type="submit" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
